"use client";

import React, { useState } from "react";
import {
  IFundHoldingsContent,
  IHolding,
  ISchemeSector,
} from "../../interfaces/AMCChartData.interface";
import { selectLoginStatus, useAppSelector } from "@repo/global-store";
import { dataLayer, CombinedClevertapProps } from "@repo/analytics/data-layer-utils";

interface Props {
  content: IFundHoldingsContent;
  fundTopHolding: IHolding[];
  fundSectorHolding: ISchemeSector[];
}

interface NoDataMessageProps {
  message?: string;
}

const NoDataMessage = ({ message = "Data not available for this fund." }: NoDataMessageProps) => {
  return <p className="py-4 text-sm text-gray-400">{message}</p>;
};

const FundHoldingsTabs = ({ content, fundTopHolding = [], fundSectorHolding = [] }: Props) => {
  const { sectorHoldings, TopHoldings, fundtitle } = content ?? {};
  const [activeTab, setActiveTab] = useState<"sector" | "top">("sector");

  const loginStatus = useAppSelector(selectLoginStatus);

  const sectorData: ISchemeSector[] = Array.isArray(fundSectorHolding) ? fundSectorHolding : [];

  const topData: IHolding[] = Array.isArray(fundTopHolding) ? fundTopHolding : [];

  const handleTabClick = (tab: "sector" | "top") => {
    setActiveTab(tab);

    const payload = {
      component: "fund_holdings_tabs",
      element: "content_cards_information_descriptive_content_list_tab_click",
      event: "click",
      event_type: "tab_click",
      destination_url: "N/A",
      section_title: fundtitle,
      cta_text:
        tab === "sector" ? sectorHoldings?.sectorholdingslabel : TopHoldings?.topholdingslabel,
      product_name: "bajaj_mutual_fund",
      category_name: "loan",
      page_type: "pdp",
      customer_tag: loginStatus,
      is_loggedin: loginStatus,
      component_position: "",
      component_id: "funds_holdings",
    };
    dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const hasSectorData = sectorData.length > 0;
  const hasTopData = topData.length > 0;
  if (!hasSectorData && !hasTopData) return null;
  if (!hasSectorData && hasTopData && activeTab === "sector") {
    setActiveTab("top");
  }

  return (
    <div className="w-full" id="fund_holdings_tabs">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 text-sm">
        {hasSectorData && (
          <button
            onClick={() => handleTabClick("sector")}
            className={`mr-6 cursor-pointer pb-2 text-xs md:text-base ${
              activeTab === "sector"
                ? "border-b-2 border-orange-500 text-orange-500"
                : "text-gray-600"
            }`}
          >
            {sectorHoldings?.sectorholdingslabel}
          </button>
        )}
        {hasTopData && (
          <button
            onClick={() => handleTabClick("top")}
            className={`cursor-pointer pb-2 text-xs md:text-base ${
              activeTab === "top"
                ? "border-b-2 border-orange-500 font-medium text-orange-500"
                : "text-bfl-blue"
            }`}
          >
            {TopHoldings?.topholdingslabel}
          </button>
        )}
      </div>

      {/* Sector Tab */}
      <div className={activeTab === "sector" ? "mt-4" : "hidden"}>
        <div className="flex justify-between pb-3 text-sm">
          <span>{sectorHoldings?.sectorname}</span>
          <span>{sectorHoldings?.allocation}</span>
        </div>

        {sectorData.length === 0 ? (
          <NoDataMessage />
        ) : (
          (sectorData?.slice(0, Math.max(sectorData.length, 6)) ?? [])?.map((item) => (
            <div
              key={item.sec}
              className="flex justify-between border-b border-gray-200 py-4 text-sm last:border-b-0"
            >
              <span>{item?.sec}</span>
              <span className="text-sm md:text-base">
                {item?.perHold != null ? `${item.perHold}%` : "—"}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Top Tab */}
      <div className={activeTab === "top" ? "mt-4" : "hidden"}>
        <div className="flex justify-between pb-3 text-sm">
          <span>{TopHoldings?.sectorname}</span>
          <span>{TopHoldings?.allocation}</span>
        </div>

        {topData.length === 0 ? (
          <NoDataMessage />
        ) : (
          (topData?.slice(0, Math.max(sectorData?.length, 6)) ?? [])?.map((item) => (
            <div
              key={item.compName}
              className="flex justify-between border-b border-gray-200 py-4 text-sm last:border-b-0"
            >
              <div className="flex flex-col">
                <span>{item?.compName}</span>
                <span className="text-xs text-gray-600 md:text-sm">{item?.indName}</span>
              </div>

              <span className="text-sm md:text-base">
                {item?.perHold != null ? `${item.perHold}%` : "—"}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FundHoldingsTabs;
